import type { Directive, DirectiveBinding } from 'vue'

interface TooltipBinding extends DirectiveBinding {
  value: {
    text: string
    position?: 'top' | 'bottom' | 'left' | 'right'
  } & string
}

interface HTMLElementCustom extends HTMLElement {
  __text: string
  __class?: string
  __hideTooltip: () => void
  __showTooltip: () => void
}

const createOrGetTooltip = () => {
  let tooltip = document.querySelector('.fb-tooltip') as HTMLDivElement | null
  if (!tooltip) {
    tooltip = document.createElement('div')
    tooltip.className = 'fb-tooltip'
    document.body.appendChild(tooltip)
  }
  return tooltip
}

const updateTooltipPosition = (tooltip: HTMLDivElement, rect: DOMRect, tooltipRect: DOMRect, position: 'top' | 'bottom' | 'left' | 'right' | 'top-right', onlyx?: boolean) => {
  let translateX = 0
  let translateY = 0

  switch (position) {
    case 'top':
      translateX = rect.left + rect.width / 2 - tooltipRect.width / 2
      translateY = rect.top - tooltipRect.height - 5
      break
    case 'top-right':
      translateX = rect.right - tooltipRect.width
      translateY = rect.top - tooltipRect.height - 5
      break
    case 'bottom':
      translateX = rect.left + rect.width / 2 - tooltipRect.width / 2
      translateY = rect.bottom + 5
      break
    case 'left':
      translateX = rect.left - tooltipRect.width - 5
      translateY = rect.top + rect.height / 2 - tooltipRect.height / 2
      break
    case 'right':
      translateX = rect.right + 5
      translateY = rect.top + rect.height / 2 - tooltipRect.height / 2
      break
  }

  if (onlyx) {
    tooltip.style.transform = `translate3d(${translateX}px, ${translateY - 20}px, 0)`
  } else {
    tooltip.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`
  }
}

const TooltipDirective: Directive = {
  mounted(el: HTMLElementCustom, binding: TooltipBinding) {
    const tooltip = createOrGetTooltip()
    let showTimeout = null

    const showTooltip = () => {
      showTimeout && clearTimeout(showTimeout)
      if (typeof binding.value === 'object' && binding.value !== null) {
        tooltip.textContent = (el.__text || el.__text === null) ? el.__text : binding.value.text
      } else {
        tooltip.textContent = (el.__text || el.__text === null) ? el.__text : binding.value
      }

      if (!tooltip.textContent) return

      if (el.__class) {
        tooltip.classList.add(el.__class)
      } else {
        tooltip.classList.remove('fb-tooltip-bgnone')
      }

      tooltip.style.display = 'block'
      tooltip.style.opacity = '0'
      const rect = el.getBoundingClientRect()
      const tooltipRect = tooltip.getBoundingClientRect()

      const translateX = rect.left + rect.width / 2 - tooltipRect.width / 2
      const translateY = rect.top - tooltipRect.height - 5 + 20

      if (!tooltip.style.transform) tooltip.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`
      tooltip.classList.remove('fb-tooltip-ani')
      showTimeout = setTimeout(() => {
        updateTooltipPosition(tooltip, rect, tooltipRect, binding.value?.position || 'top', true)
        const sc = setTimeout(() => {
          clearTimeout(sc)
          tooltip.style.opacity = '1'
          tooltip.classList.add('fb-tooltip-ani')
          updateTooltipPosition(tooltip, rect, tooltipRect, binding.value?.position || 'top')
        }, 30)
      }, 1)
    }

    const hideTooltip = () => {
      showTimeout && clearTimeout(showTimeout)

      tooltip.classList.remove('fb-tooltip-ani')
      tooltip.style.display = 'none'
    }

    el.addEventListener('mouseenter', showTooltip)
    el.addEventListener('mouseleave', hideTooltip)
    el.addEventListener('click', hideTooltip)

    el.__hideTooltip = hideTooltip
    el.__showTooltip = showTooltip

    if (typeof binding.value === 'object' && binding.value !== null) {
      el.__text = binding.value?.text || null
      el.__class = binding.value?.class || ''
    } else {
      el.__text = binding.value || null
    }
  },
  updated(el: HTMLElementCustom, binding: TooltipBinding) {
    if (typeof binding.value === 'object' && binding.value !== null) {
      el.__text = binding.value?.text || null
      el.__class = binding.value?.class || ''
    } else {
      el.__text = binding.value || null
    }
  },
  unmounted(el: HTMLElementCustom) {
    el.__hideTooltip && el.__hideTooltip()
    el.removeEventListener('mouseenter', el.__showTooltip)
    el.removeEventListener('mouseleave', el.__hideTooltip)
    el.removeEventListener('click', el.__hideTooltip)
  },
}

export default TooltipDirective
